import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../components/AuthContext';
import {
  canSwitchBranch as canSwitchBranchPermission,
  canAdjustInventory,
  canSeeAllBranches as canSeeAllBranchesPermission,
} from '../../lib/permissions';
import { categoryLabelText } from '../../lib/categoryLabel';
import { DESKTOP_COLUMN, MOBILE_BREAKPOINT, WORKBENCH_BREAKPOINT } from '../../lib/layout';
import { getStockStatus } from '../../lib/stockStatus';
import RequestTransferSheet from './RequestTransferSheet';
import MakeForShelfSheet from './MakeForShelfSheet';
import Overlay from '../../components/Overlay';
import {
  resolveFulfilment, bestBranchBySurplus, stockPathways, realPathways, tapTarget, rowControlLabel, rowOpensMore, rowShortBy, pathwayButtonLabel,
  openAsksFor, alreadyAskedSentence, OPEN_ASK_STATUSES, promisedFromShelf, transfersInHand,
} from '../../lib/branchTransfers';
import { cutOff } from '../../lib/readLimit';
import { onItsWayTo, onItsWayLine, isCovered, ON_ITS_WAY_STATUSES } from '../../lib/transferLoads';
import { canConfirmSupply } from '../../lib/canApprove';
// #689 D19 -- the Transfer work row, counted by the Transfers tabs' own rules.
import { fetchTransferWork, transferJobs, shortcutChips, shortcutHeading, SHORTCUT_DOT } from '../../lib/transferWork';
import { useTier, controlHeight } from '../../lib/tiers';
import OnboardingCallout from '../../components/OnboardingCallout';
import {
  IconPackageImport,
  IconPackageExport,
  IconAdjustmentsHorizontal,
  IconLayoutList,
  IconPhoto,
  IconPhotoOff,
  IconChevronDown,
  IconChevronUp,
  IconShoppingCartPlus,
  IconChevronRight,
  IconCheck,
} from '@tabler/icons-react';

const BRANCHES = [
  { id: 1, name: 'Dallas' },
  { id: 2, name: 'Houston' },
  { id: 3, name: 'Austin' },
  { id: 4, name: 'San Antonio' },
];
const BRANCH_NAMES = Object.fromEntries(BRANCHES.map(b => [String(b.id), b.name]));

// #580 part (b), Session 79.21b -- MEASURED, and Low failed. #BA7517 read 3.72
// on white and 3.39 on its own pill against the 4.5 normal text needs; Low is
// now the app's one amber (RecordProductionSheet's AMBER), 5.92 on white. Out
// was already 7.07 and is unchanged.
const STATUS_COLORS = {
  Low:  { color: '#8A5A08', bg: '#FFF8EC', border: '#F3D9A8', label: 'Low' },
  Out:  { color: '#A32D2D', bg: '#FFEBEE', border: '#FFCDD2', label: 'Out' },
};

// White on the locked inventory green measures 3.39 and fails; the darkened
// variant measures 6.20 (550/551 F4, 553 F4).
const MAKE_GREEN = '#0F6E56';

// #668, Session 79.22b.4 -- A COLOUR SAYS WHO SUPPLIES IT, and nothing else.
// APPROVED BY COLBY, Sep 25: "#668: approved as drawn."
//
// 550/551's classes gave the colour two jobs. Green meant "a branch makes it"
// AND "the only way" (a lone Request was filled green), and every button on a
// row went white once anything was on its way (371 D19's step-down) -- so the
// same "Ask Houston" was green on one row and white on the next, and the cause
// was a line somewhere else in the row. Colby asked twice why, which was the
// finding: a screen that cannot be learned by using it.
//
// Now: green is made by a D&R branch, blue is another branch's shelf, amber is
// Purchasing (its domain colour's tint), and a CHOICE -- "2 ways to get these"
// -- is no supplier at all, so it is a plain outline. Make stays the only FILLED
// control (550/551 D4: making is always the first choice). The step-down moved
// out of the colour and into words -- see alreadyComingLine() -- and the key
// under the list header says all three, which is true now that each means one
// thing. Every text measured against its own fill, floor 4.5: 6.20, 7.59, 8.72,
// 11.4.
const CONTROL_LOOK = {
  make:   { background: MAKE_GREEN, color: '#FFFFFF', border: `1px solid ${MAKE_GREEN}` },
  xfer:   { background: '#EAF2FA', color: '#1F4E7E', border: '1px solid #C3DAF0' },
  buy:    { background: '#FAEEDA', color: '#633806', border: '1px solid #F3D9A8' },
  choice: { background: '#FFFFFF', color: '#3A3A3A', border: '1px solid #9A9A9A' },
};

// Which look a pathway gets: its supplier, and only its supplier.
function lookFor(key) {
  if (key === 'make' || key === 'make_own') return 'make';
  if (key === 'transfer') return 'xfer';
  return 'buy';
}

// 550/551 F1 -- a failed check of the other branches is said, never silent,
// and Request is never hidden behind it.
const CHECK_FAILED = 'We could not check the other branches just now. Request still works.';

// #357 — this file's local getStatus() tested the reorder point before the
// quantity, so an item at zero with no threshold returned null and this page
// reported "All stocked up" over it. The rule now lives in one place:
// src/lib/stockStatus.js.

// #370, gate decision C. The default order answers "what do I do now," not
// "where is this item." Out before Low, then furthest below its reorder point
// first. An Out row with no reorder point has no deficit to measure, so it
// falls back to quantity -- and for an Out row that is zero or negative, so a
// negative count sorts above an empty shelf, which is the right emphasis.
function statusRank(status) {
  if (status === 'Out') return 2;
  if (status === 'Low') return 1;
  return 0;
}

function deficitOf(row) {
  const rp = row.reorder_point;
  if (rp === null || rp === undefined) return 0;
  return Number(rp) - Number(row.qty_on_hand);
}

function branchNameOf(id) {
  return BRANCHES.find(b => b.id === Number(id))?.name || '--';
}

export default function InventoryStockHome({ setActive, setScanItemId, setBatchRequestItems, openTransfersAt = null }) {
  const auth = useAuth();
  const { profile, isAdmin, isManager, isExecutive, isTeamMember, isPurchasing } = auth;
  // #689's second half, gate 665 D19, Session 79.22b.5.1 -- TRANSFER WORK ON
  // THE FRONT DOOR. "4 to make · 1 to load": each chip opens Transfers on its
  // tab, so a person's transfer job is two taps from here. Counted by the same
  // rules as the tab pills (src/lib/transferWork.js), for the person's OWN
  // branch -- whatever branch this page is showing, a job is always done at
  // your own. Only jobs this person can do; nothing at all while counting, on
  // a quiet day, or if a read fails, so it never shows a zero that reads as
  // "no work" (gate F1).
  const [work, setWork] = useState(null);
  const authRef = useRef(auth);
  authRef.current = auth;
  useEffect(() => {
    let alive = true;
    const b = profile?.branch_id;
    if (b == null) { setWork(null); return undefined; }
    fetchTransferWork({ branchId: b, auth: authRef.current })
      .then(w => { if (alive) setWork(w); })
      .catch(() => { if (alive) setWork({ ok: false }); });
    return () => { alive = false; };
  }, [profile]);
  const transferShortcuts = useMemo(() => {
    if (!work?.ok || profile?.branch_id == null) return [];
    return shortcutChips(transferJobs({
      lists: work.lists, openLoads: work.openLoads, myBranch: profile.branch_id,
      profile, auth: authRef.current, drivers: work.drivers,
    }));
  }, [work, profile]);
  // #356. A bare window.innerWidth read is correct once, on mount, and silently
  // wrong for the rest of the session -- that was #64, and #164 found it again.
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);
  // #370, gate decision W2. Three tiers, not two: the phone layout below
  // MOBILE_BREAKPOINT, the bounded single column #356 shipped in between, and
  // the workbench here.
  const [isWorkbench, setIsWorkbench] = useState(window.innerWidth >= WORKBENCH_BREAKPOINT);
  // #417 D24, corrected by Colby at 79.15's walk-through: the sheet/dialog
  // boundary is 1024, NOT 768. A tablet is CARRIED, held standing and sometimes
  // gloved, so it takes the phone's bottom sheet rather than a centre dialog --
  // and `isMobile` above would have handed a 900px tablet a dialog. Reading
  // `phoneLayout` rather than comparing widths is D3: the module never gives a
  // caller the chance to write `<` or `<=` wrongly.
  // `coarse` decides control SIZE (#417 D2): a finger gets 40-48px at any
  // width, a mouse 32px.
  const { phoneLayout, coarse } = useTier();

  // #518, Session 79.7 — the low-stock list is capped, and this is the whole fix.
  //
  // WHAT COLBY SAW: Dallas with 32 low-stock items rendered 54 blocks, because
  // 22 of them also drew a full-width transfer banner underneath. Everything
  // below the list — and the list's own end — was several screens of scrolling
  // away. On the app's own priority surface, where the standard is "every action
  // in 2 taps, under 3 seconds".
  //
  // SIX, and the number is his. It keeps the way out above the fold on a phone,
  // which is the point: the complaint was never the list's length, it was that
  // the escape from it lived at the bottom of the thing you were escaping.
  //
  // EXPANDS IN PLACE RATHER THAN LINKING TO A FULL LIST SCREEN. There is no
  // all-low-stock screen and building one would be a second surface answering a
  // question this one already answers — the #391 mistake. Expanding also keeps
  // every transfer entry point reachable, which a hard cap would quietly break:
  // this list is currently the ONLY place "Ask Houston" appears on mobile.
  //
  // Nielsen Norman tested this exact control: a view-all option helped some
  // users and bothered none who ignored it, while omitting it produced
  // complaints. It is the same shape SAP Fiori's overview page uses — top N on
  // the card, the rest one action away.
  const LOW_STOCK_PREVIEW = 6;
  const [showAllLowStock, setShowAllLowStock] = useState(false);
  const [branchId, setBranchId] = useState(null);
  const [showBranchPicker, setShowBranchPicker] = useState(false);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [elsewhere, setElsewhere] = useState({});
  const [categories, setCategories] = useState([]);
  const [loadingStock, setLoadingStock] = useState(true);
  const [selectMode, setSelectMode] = useState(false);
  // #371, Session 79.6. The row a person is standing on is where the recovery
  // path belongs -- gate D19 is explicit that dropping the low-stock row would
  // HIDE it, because the transfer action lives there.
  const [transferRow, setTransferRow] = useState(null);
  // #550 D8 -- the row whose pathway chooser is open, or null.
  const [pathwayRow, setPathwayRow] = useState(null);
  // #517 / D53 -- several items in one ask. Separate from transferRow so the
  // single-item path keeps its exact shape, including the remount key that
  // #516 exists to enforce.
  const [transferRows, setTransferRows] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [viewAll, setViewAll] = useState(false);
  const [sort, setSort] = useState({ key: 'status', dir: 'desc' });
  // #595 -- a list that FAILED to load is not an empty list. It used to fall
  // through to "All stocked up" under a green heading.
  const [listFailed, setListFailed] = useState(false);
  // 550/551 F1 -- each secondary check reports its own failure, and none of
  // them hides Request.
  const [elsewhereFailed, setElsewhereFailed] = useState(false);
  const [categoriesFailed, setCategoriesFailed] = useState(false);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  // #582 and gate 553 D25 -- open transfers INTO the branch(es) in view, from
  // which onItsWayTo() derives what is coming, per item.
  const [incoming, setIncoming] = useState([]);
  const [incomingFailed, setIncomingFailed] = useState(false);
  // #582, as Colby corrected it -- the open asks for the low items from ANY
  // branch, keyed item:supplier, so "Ask Austin again" is only ever said
  // when somebody has. A failed read leaves it empty: plain words, never a
  // false "again".
  const [openAsks, setOpenAsks] = useState({});
  // #580 part (a) -- the row whose Make these ourselves sheet is open.
  const [makeRow, setMakeRow] = useState(null);

  // First-run callouts gate, Wave 1 — inv_home_first. Anchored to the section
  // label rather than a row: the tip is about what the whole list IS, and rows
  // come and go with the data. An anchor that can vanish is #38's second failure
  // mode, which fails silently.
  const lowStockLabelRef = useRef(null);

  const canSwitchBranch = canSwitchBranchPermission({ isAdmin, isManager, isExecutive });
  // #369 — passes the whole auth object, not the manager triple. See permissions.js.
  const canAdjust = canAdjustInventory({ isAdmin, isManager, isExecutive, isTeamMember, isPurchasing, profile });
  // #370, gate decision W8 — admin, executive, manager and purchasing, with
  // purchasing included by Colby's ruling at 78f. See callout E, overturned.
  const canSeeAllBranches = canSeeAllBranchesPermission({ isAdmin, isManager, isExecutive, isPurchasing });

  // Derived rather than stored, deliberately. A window narrowed below the
  // workbench tier falls back to its own branch with no state to reconcile, and
  // widening it again restores the choice. Storing "am I in all-branches mode"
  // as an independent fact is how a screen ends up rendering a Branch column it
  // has no Branch data for.
  const allBranches = isWorkbench && canSeeAllBranches && viewAll;

  useEffect(() => {
    if (profile?.branch_id) setBranchId(profile.branch_id);
  }, [profile]);

  const branchName = BRANCHES.find(b => b.id === branchId)?.name || 'Loading...';
  const scopeLabel = allBranches ? 'all branches' : branchName;

  const loadLowStock = useCallback(async () => {
    if (!allBranches && !branchId) return;
    setLoadingStock(true);

    let query = supabase
      .from('inventory_stock')
      .select(`
        id, item_id, branch_id, qty_on_hand, reorder_point, reorder_qty,
        inventory_items (
          id, name, sku, uom, image_url, is_active,
          inventory_categories ( id, name, parent_id )
        )
      `);
    if (!allBranches) query = query.eq('branch_id', branchId);

    const { data, error } = await query;

    // #595 -- A FAILED LOAD IS NOT AN EMPTY LIST. This returned with the list
    // still empty, so the screen fell through to "All stocked up" under a green
    // heading: a false all-clear on a weak phone connection at the rack.
    if (error) { setListFailed(true); setLowStockItems([]); setLoadingStock(false); return; }
    setListFailed(false);

    const flagged = (data || []).filter(row => {
      if (!row.inventory_items) return false;
      if (!row.inventory_items.is_active) return false;
      const status = getStockStatus(row.qty_on_hand, row.reorder_point);
      return status === 'Low' || status === 'Out';
    });

    setLowStockItems(flagged);

    // #370, gate decision W12 and callout H. What every other branch holds of
    // the same item -- the fact that makes a transfer thinkable instead of a
    // second screen. Bounded by the low-stock list rather than by the catalog.
    //
    // #551, Session 79.15c, gate D12: THE WIDTH GUARD IS GONE. This read
    // `if (isWorkbench && ...)`, and the reason it gave was true when written --
    // "the only tier with a column to put it in, and a phone at the rack should
    // pay nothing for it." The Elsewhere COLUMN was the only consumer and that
    // column only exists on the workbench.
    //
    // Then #518 added the CHIP, which is a second consumer and exists at EVERY
    // width, and nobody re-checked the fetch. Below 1180px `elsewhere` was set
    // empty, so othersFor() returned [], so transferHintFor() returned null for
    // any purchased item, so no chip rendered -- and nothing said why. Dallas
    // out of an item with forty sitting at Austin had no way to ask for them on
    // a phone. A made item was unaffected, because its chip comes from the
    // category tree rather than from this fetch, which is exactly why the screen
    // looked like it offered transfers: it did, for one class of item only.
    //
    // #558: reorder_point is SELECTED HERE and it is the blocking half of that
    // issue. Ranking by the biggest pile picks the wrong branch -- Austin and
    // Houston both holding 100 are not equal if Austin reorders at 50 and
    // Houston at 75. The surplus cannot be computed from qty alone, and the
    // other branches' rows never carried the floor.
    if (flagged.length > 0) {
      const itemIds = Array.from(new Set(flagged.map(r => r.item_id)));
      // #582 / 553 D25 -- WHAT IS ON ITS WAY, fetched beside the other branches'
      // stock and at EVERY width (#551's lesson: no fetch guarded on width).
      // Every open transfer INTO the branch in view; onItsWayTo() does the
      // arithmetic, the same per-line sums the Transfers screen uses.
      // #608 / gate 675 D15, Session 79.22b.5.3 -- READ FROM THE WORK IN HAND,
      // the Transfers page's own rule. Naming closed_unfilled here brought back
      // every "nothing more coming" transfer the branch ever had, a list that
      // only grew; transfersInHand() keeps one only while a truck of it is out.
      let incomingQuery = transfersInHand(`
          id, transfer_number, status, from_branch_id, to_branch_id, fulfilment_mode,
          confirmed_date, requested_at,
          branch_transfer_items ( item_id, qty_requested, qty_ready, qty_shipped, qty_received, line_status, promised_date )
        `)
        // #600 -- closed_unfilled too: a truck already out when the rest was
        // stopped still arrives, and onItsWayTo() counts only what is on it.
        .in('status', ON_ITS_WAY_STATUSES);
      if (!allBranches) incomingQuery = incomingQuery.eq('to_branch_id', branchId);
      const [{ data: others, error: othersErr }, openRead, askedRead] = await Promise.all([
        supabase
          .from('inventory_stock')
          .select('item_id, branch_id, qty_on_hand, reorder_point')
          .in('item_id', itemIds)
          .gt('qty_on_hand', 0),
        incomingQuery,
        // #582 -- every open ask for these items, whoever asked and whoever
        // was asked. One hop from the transfer to its lines, filtered on the
        // line, which PostgREST honours (the two-hop trap is not this).
        // #721 -- qty_ready and qty_shipped ride along, so the same read says
        // what each branch has already PROMISED of these items (approved and
        // moving transfers are both open asks), with no second round trip.
        supabase
          .from('branch_transfers')
          .select('id, status, from_branch_id, to_branch_id, branch_transfer_items!inner ( item_id, qty_requested, qty_ready, qty_shipped, line_status )', { count: 'exact' })
          .in('status', OPEN_ASK_STATUSES)
          .in('branch_transfer_items.item_id', itemIds),
      ]);
      // #608 / gate 675 D17 -- A LIST CUT AT THE DATABASE'S ROW LIMIT IS A
      // FAILED CHECK, never a smaller answer: an "on its way" or "already
      // asked" figure missing some transfers is a confident wrong number. The
      // failed-check paths below already say so in words (550/551 F1).
      const open = openRead.data;
      const openErr = openRead.error || cutOff(openRead);
      const asked = askedRead.data;
      const askedErr = askedRead.error || cutOff(askedRead);
      setOpenAsks(askedErr ? {} : openAsksFor(asked || []));
      // #721 -- what each other branch has already promised. When this read
      // fails, the other branches' figures are NOT shown (below): taken as
      // nothing promised they would overstate what a branch can send, which
      // is the defect itself. Found by Grok's review at 79.22b.5.1b.
      const promised = askedErr ? {} : promisedFromShelf(asked || []);
      // 550/551 F1 -- a failed check is SAID, and Request stays reachable
      // because an empty map offers it on every row.
      setElsewhereFailed(!!othersErr || !!askedErr);
      setIncomingFailed(!!openErr);
      setIncoming(openErr ? [] : (open || []));
      const map = {};
      (others || []).forEach(o => {
        if (!map[o.item_id]) map[o.item_id] = [];
        map[o.item_id].push({
          branchId: Number(o.branch_id),
          qty: Number(o.qty_on_hand),
          // null is NOT zero and must not be coerced here -- surplusOf() decides
          // what an unset floor means, in one place, per Colby's ruling.
          reorderPoint: o.reorder_point == null ? null : Number(o.reorder_point),
          // #721 -- already promised to other transfers. surplusOf() takes it
          // off, so the branch suggested and every "can spare" figure follow.
          promised: Number(promised[`${o.item_id}:${o.branch_id}`]) || 0,
        });
      });
      // #721 -- with the promises unknown, the other branches are left out
      // exactly as when their stock could not be read: the note says the
      // check failed, and Request stays reachable on every row (550/551 F1).
      // Colby's ruling at 79.22b.5.1b: the note shows instead of the figures.
      setElsewhere(askedErr ? {} : map);
    } else {
      setElsewhere({});
      setIncoming([]);
      setOpenAsks({});
      setElsewhereFailed(false);
      setIncomingFailed(false);
    }

    setLoadingStock(false);
  }, [branchId, allBranches]);   // #551: no longer re-fetches on width

  useEffect(() => { loadLowStock(); }, [loadLowStock]);

  // 74s. Flat category list, so a low-stock card can show its parent category.
  const loadCategories = useCallback(async () => {
    const { data, error } = await supabase
      .from('inventory_categories')
      // #371, Session 79.6. The two manufacturing columns ride along because
      // resolveFulfilment() walks this list to decide whether another branch
      // MAKES an item or holds it on a shelf. Without them every row reads as
      // shelf stock and a stakes request would silently ask Houston to pull
      // them off a shelf it does not have.
      .select('id, name, parent_id, manufactured_at_branch_id, manufacturing_department_id')
      .order('name');
    // 550/551 F1: with no category list every row falls back to Request, which
    // is safe by construction -- and the screen says the check failed rather
    // than quietly offering Purchasing for something a branch makes.
    setCategoriesFailed(!!error);
    setCategories(data || []);
    setCategoriesLoaded(true);
  }, []);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
      setIsWorkbench(window.innerWidth >= WORKBENCH_BREAKPOINT);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // #356. Stock Home was a phone layout stretched to the window: the two-up action
  // grid had nothing constraining it, so 'Receive stock' rendered as a green bar most
  // of a metre wide with its label in the far-left corner, and every low-stock row put
  // the item name at one edge and the quantity at the other. Same defect class as #282
  // one screen over. The measure is DESKTOP_COLUMN rather than a number invented here,
  // which is what #282's own entry asks for.
  //
  // #370 builds the workbench on top of this. The measure is unchanged and still shared:
  // 236 rail + 14 gutter + 850 table is exactly DESKTOP_COLUMN.
  const column = { maxWidth: isMobile ? 'none' : DESKTOP_COLUMN, margin: '0 auto', width: '100%', boxSizing: 'border-box' };

  // 74s. Was reading the nested self-embed, which returns children rather than the
  // parent, so low-stock cards showed the subcategory alone. See src/lib/categoryLabel.js.
  function getCategoryLabel(cat) {
    return categoryLabelText(cat, categories, { fallback: '' });
  }

  function othersFor(row) {
    return (elsewhere[row.item_id] || []).filter(o => o.branchId !== Number(row.branch_id));
  }

  function elsewhereTotal(row) {
    return othersFor(row).reduce((sum, o) => sum + o.qty, 0);
  }

  // #558, Session 79.15c. WHICH branch to ask, in ONE place, because the answer
  // was previously computed twice -- in transferHintFor() and again in
  // transferChipFor() -- and two copies of a rule drift in exactly one half.
  //
  // Colby's rule: "technically that is the branch with the most material to
  // spare... if branch a and b both have 100 on hand and a's reorder point is 50
  // and b's is 75 then branch a has the most to spare." So SURPLUS, not pile.
  //
  // THE MIXED CASE IS HIS RULING at 79.15b's decision gate and it is deliberate:
  // a branch with NO reorder point is ranked on its whole on-hand and may
  // out-rank one that has set a floor. It reads wrong at first glance -- a
  // branch that never set a threshold looks more generous than one that did --
  // and the alternatives are worse. Treating an unset floor as "unknown" and
  // dropping the branch hides real stock; guessing a floor invents a number
  // nobody entered. The known cost is accepted and revisited only if #357 moves.
  // MOVED to src/lib/branchTransfers.js at Session 79.15i, gate #565 D3, and
  // this comment stays because the REASON is the interesting part. The request
  // sheet became a third consumer of the same question and was still ranking by
  // the raw pile, so the sheet and this screen could recommend different
  // branches for the same item -- the exact drift the paragraph above was
  // written to stop, one screen further out. Imported now rather than copied.

  // Greatest surplus wins; the raw pile breaks a tie so the order is stable
  // rather than dependent on row order, and the branch name breaks THAT tie for
  // the same reason. A list that reorders itself between renders reads as a bug.
  function bestSourceFor(row) {
    return bestBranchBySurplus(othersFor(row), branchNameOf);
  }

  // #371, Session 79.6. One line saying why a transfer is worth asking for, or
  // null when it is not worth offering at all.
  //
  // The category id comes off the NESTED embed rather than a category_id column,
  // because that is what this page's own query selects -- reading
  // item.category_id here returns undefined and every row would read as shelf
  // stock, which is the silent-wrong-answer shape rather than an error.
  function transferHintFor(row) {
    const item = row.inventory_items;
    const f = resolveFulfilment(categories, item?.inventory_categories?.id);
    if (f.mode === 'make') {
      // #580 part (b): the maker's own row says so too — 550/551 Screen E's
      // "Frames · we make these here". It used to say nothing, because the row
      // offered nothing a maker could use.
      // "We" only for somebody AT that branch: a manager looking at another
      // branch's Stock Home reads "Houston makes these".
      if (String(f.branchId) === String(row.branch_id)
          && String(profile?.branch_id) === String(row.branch_id)) return 'we make these here';
      return `${branchNameOf(f.branchId)} makes these`;
    }
    // #558: RANKED by surplus, but the sentence still reports what they HAVE.
    // "12 at Austin" is the quantity a person weighs before asking; the surplus
    // is how we chose Austin, not what we are offering them up to.
    const best = bestSourceFor(row);
    if (!best) return null;
    return `${best.qty} at ${branchNameOf(best.branchId)}`;
  }

  // #518 -- the CHIP label, deliberately separate from transferHintFor() rather
  // than trimmed from it. The banner said "Houston makes these" or "12 at
  // Austin"; a chip on the row has no room for either and does not need it,
  // because the row already names the item. It needs the one fact the row does
  // not carry: WHO to ask.
  //
  // Both are kept because they answer different questions. Folding the sentence
  // into the chip would lose the shelf case entirely -- "12 at Austin" is a
  // quantity a person weighs before asking, and it belongs on the full list
  // rather than on a 90px chip.
  function transferChipFor(row) {
    if (!transferHintFor(row)) return null;
    const item = row.inventory_items;
    const f = resolveFulfilment(categories, item?.inventory_categories?.id);
    // A branch never asks ANOTHER branch for what it makes itself: the batch
    // "Ask another branch instead" must not appear for its own stakes.
    if (f.mode === 'make' && String(f.branchId) === String(row.branch_id)) return null;
    if (f.mode === 'make') return `Ask ${branchNameOf(f.branchId)}`;
    const best = bestSourceFor(row);   // #558: same rule as the hint, one source
    if (!best) return null;
    return `Ask ${branchNameOf(best.branchId)}`;
  }

  // ==========================================================================
  // #550, Session 79.15c. THE PATHWAY MODEL -- gate decisions D1 to D19.
  //
  // WHAT WAS WRONG. A low-stock row carried TWO ways to ask for more of the
  // same thing, and they went to completely different places. Tapping the row
  // called openRestockModal(), four lines that consulted NOTHING -- not the
  // category, not the fulfilment mode, not whether a branch manufactures the
  // item. Tapping the chip beside it raised a transfer. So for a Houston-made
  // frame the bigger, more obvious target asked PURCHASING TO BUY, from an
  // outside vendor, a product D&R makes itself. Nothing downstream catches it,
  // because the request does not say the item is made internally.
  //
  // THE MODEL. A row offers exactly the pathways that are actually available
  // for that item (D2) -- never a fixed set with some greyed out. Three of
  // them, named as Oracle names them (D3):
  //
  //   MAKE      someone manufactures it. Master data decides this, so it is
  //             always the primary action (D4).
  //   TRANSFER  somebody has spare on a shelf. True this morning and false this
  //             afternoon, so it is offered and never primary (D5), and it
  //             always carries the quantity so the ask can be judged.
  //   BUY       purchasing raises an order. Always available for a purchased
  //             item and it is the fallback (D6), so the list is never empty.
  //
  // ONE pathway means the tap goes straight to it (D7) -- a chooser with one
  // option is a tax. More than one opens the chooser (D8).
  // #580, Session 79.21b -- THE MODEL MOVED TO src/lib/branchTransfers.js as
  // stockPathways(), where it is pure and tested, which is what 550/551 F5
  // asked for. Two things changed in it and both are #580:
  //
  //   (a) the MAKER'S OWN ROW offers Make these ourselves (gate 553 D21-D27).
  //       It used to drop its make option and keep only D14's exception, and
  //       because one option meant a single tap went straight to it, a Houston
  //       row for something Houston makes opened a PURCHASE REQUEST.
  //   (b) the words are gate 550/551's approved copy table, typed rather than
  //       composed, as Colby ruled.
  function pathwaysFor(row) {
    return stockPathways({ row, categories, others: othersFor(row), branchNameOf, viewerBranchId: profile?.branch_id ?? null });
  }

  // The on-its-way entries for one row, from the one shared derivation.
  const incomingByBranch = useMemo(() => {
    const out = {};
    const ids = new Set(lowStockItems.map(r => String(r.branch_id)));
    ids.forEach(b => { out[b] = onItsWayTo(incoming, b); });
    return out;
  }, [incoming, lowStockItems]);

  function comingFor(row) {
    return incomingByBranch[String(row.branch_id)]?.[row.item_id] || [];
  }

  // The pathways are ready to show once the category tree has answered (or
  // failed). Until then the control holds its place, greyed, with no label
  // (550/551 callout 11) -- a label that changes a second later is how
  // "Request" briefly offered Purchasing for a Houston frame.
  const pathwaysReady = categoriesLoaded;

  const checkFailed = elsewhereFailed || categoriesFailed || incomingFailed;

  // D18 -- every destination names which pathway it is. A single tap can land in
  // any of three places, so each sheet says what it will do and who receives it.
  function openPathway(row, key) {
    setPathwayRow(null);
    if (key === 'buy') { openRestockModal(row); return; }
    if (key === 'make_own') { setMakeRow(row); return; }
    setTransferRow(row);
  }

  const sortedItems = useMemo(() => {
    const rows = [...lowStockItems];
    const dir = sort.dir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      let cmp = 0;
      if (sort.key === 'status') {
        const sa = statusRank(getStockStatus(a.qty_on_hand, a.reorder_point));
        const sb = statusRank(getStockStatus(b.qty_on_hand, b.reorder_point));
        cmp = sa - sb;
        if (cmp === 0) cmp = deficitOf(a) - deficitOf(b);
        if (cmp === 0) cmp = Number(b.qty_on_hand) - Number(a.qty_on_hand);
      } else if (sort.key === 'item') {
        cmp = String(a.inventory_items?.name || '').localeCompare(String(b.inventory_items?.name || ''));
      } else if (sort.key === 'branch') {
        cmp = branchNameOf(a.branch_id).localeCompare(branchNameOf(b.branch_id));
      } else if (sort.key === 'qty') {
        cmp = Number(a.qty_on_hand) - Number(b.qty_on_hand);
      } else if (sort.key === 'reorder') {
        // A null reorder point sorts last in BOTH directions. It is not a low
        // number, it is the absence of one -- letting it compare as zero would
        // stack every unthresholded item at the top of an ascending sort and
        // read as "these are the most urgent," which is the exact misreading
        // #357 was logged for.
        const ra = a.reorder_point === null || a.reorder_point === undefined ? null : Number(a.reorder_point);
        const rb = b.reorder_point === null || b.reorder_point === undefined ? null : Number(b.reorder_point);
        if (ra === null && rb === null) cmp = 0;
        else if (ra === null) return 1;
        else if (rb === null) return -1;
        else cmp = ra - rb;
      } else if (sort.key === 'elsewhere') {
        cmp = elsewhereTotal(a) - elsewhereTotal(b);
      }
      return cmp * dir;
    });
    return rows;
    // Deps are the three things the order actually depends on. othersFor and
    // elsewhereTotal are plain closures over `elsewhere`, which is listed, so
    // the elsewhere sort recomputes when that map arrives rather than holding a
    // stale grouping. This project's eslint config does not carry
    // react-hooks/exhaustive-deps -- a disable comment for it fails the build.
  }, [lowStockItems, sort, elsewhere]);

  // #518 — what actually renders. Sorting happens first and the cap second, on
  // purpose: the six shown are the six the current sort puts at the top, so
  // "worst first" still means worst first rather than "worst of an arbitrary
  // six". Re-sorting while collapsed changes which six you see, which is what a
  // person expects from a preview.
  const visibleItems = showAllLowStock ? sortedItems : sortedItems.slice(0, LOW_STOCK_PREVIEW);
  const hiddenCount = sortedItems.length - visibleItems.length;

  function toggleSort(key) {
    setSort(prev => prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: key === 'item' || key === 'branch' ? 'asc' : 'desc' });
  }

  // #684, Session 79.22b.4 -- EVERY purchase this screen opens is deliberate:
  // a bought item's own route, or "Buy from a vendor instead?" on a made one.
  // It says so ('buy'), because the request screen now sends a made item to its
  // maker unless told the person chose to buy it.
  function openRestockModal(stockRow) {
    setBatchRequestItems(null);
    setScanItemId(stockRow.inventory_items.id, 'buy');
    setActive('inv_request');
  }

  // #518, Session 79.7 -- LONG-PRESS A ROW TO START A MULTI-ITEM REQUEST.
  // Colby's idea, and it is the platform convention rather than an invention:
  // Gmail, Photos and Files all enter multi-select exactly this way, so a field
  // user already has the muscle memory.
  //
  // IT DOES NOT REPLACE THE BUTTON, and that is the part worth stating. A
  // long-press has no visible affordance, so on its own it is undiscoverable --
  // which is why every app that offers it also keeps a visible entry point. The
  // renamed "Request several" stays; this is a shortcut for people who find it.
  //
  // TWO THINGS THAT ARE EASY TO GET WRONG AND BOTH ARE HANDLED HERE:
  //
  //   1. SCROLLING MUST NOT TRIGGER IT. A finger resting on a row for half a
  //      second while the list moves is a scroll, not a press. Any movement past
  //      8px cancels the timer, so the list stays scrollable -- without this the
  //      feature makes the page feel broken rather than clever.
  //   2. THE CLICK THAT FOLLOWS MUST BE SWALLOWED. A touchend after a long press
  //      still fires a click, which would open the restock modal on top of the
  //      selection the press just made. The flag is read and cleared by
  //      handleCardClick.
  const longPressTimer = useRef(null);
  const longPressFired = useRef(false);
  const touchOrigin = useRef({ x: 0, y: 0 });

  useEffect(() => () => clearTimeout(longPressTimer.current), []);

  function startLongPress(row, e) {
    if (selectMode) return;
    const p = e.touches && e.touches[0];
    touchOrigin.current = { x: p ? p.clientX : 0, y: p ? p.clientY : 0 };
    longPressFired.current = false;
    clearTimeout(longPressTimer.current);
    longPressTimer.current = setTimeout(() => {
      longPressFired.current = true;
      setSelectMode(true);
      setSelectedIds([row.id]);
      // A press that changes the screen with no sound and no movement feels
      // like a misfire. Guarded because iOS Safari does not implement it.
      if (navigator.vibrate) navigator.vibrate(12);
    }, 500);
  }

  function moveLongPress(e) {
    const p = e.touches && e.touches[0];
    if (!p) return;
    if (Math.abs(p.clientX - touchOrigin.current.x) > 8 ||
        Math.abs(p.clientY - touchOrigin.current.y) > 8) {
      clearTimeout(longPressTimer.current);
    }
  }

  function endLongPress() {
    clearTimeout(longPressTimer.current);
  }

  function toggleSelectMode() {
    setSelectMode(v => !v);
    setSelectedIds([]);
  }

  function toggleSelectItem(rowId) {
    setSelectedIds(prev => prev.includes(rowId) ? prev.filter(id => id !== rowId) : [...prev, rowId]);
  }

  function handleCardClick(row) {
    // #518 -- see startLongPress. A long press is followed by a real click, and
    // without this the modal opens over the selection the press just made.
    if (longPressFired.current) { longPressFired.current = false; return; }
    if (selectMode) {
      toggleSelectItem(row.id);
      return;
    }
    // #550 D1. THIS USED TO CALL openRestockModal() UNCONDITIONALLY, which is
    // the whole defect: the biggest target on the row raised a purchase request
    // for an item Houston manufactures. The tap now asks what is actually
    // available first.
    //
    // #580: D7 COUNTS REAL PATHWAYS ONLY. The exception used to count, so on a
    // maker's own row -- whose only other option had been removed -- it was the
    // single thing a tap reached. tapTarget() also keeps the chooser when the
    // one real pathway's sheet would strand the exception (acceptance A1).
    if (!pathwaysReady) return;
    const target = tapTarget(pathwaysFor(row));
    if (target.open) { openPathway(row, target.open); return; }   // D7
    setPathwayRow(row);                                           // D8
  }

  // #517 / D53 -- hand the whole selection to the transfer sheet, which groups
  // it by sending branch and raises one request per branch.
  function startBatchTransfer() {
    const rows = lowStockItems.filter(r => selectedIds.includes(r.id));
    setTransferRows(rows.map(row => ({
      id: row.inventory_items?.id,
      name: row.inventory_items?.name,
      sku: row.inventory_items?.sku,
      uom: row.inventory_items?.uom,
      category_id: row.inventory_items?.inventory_categories?.id,
      // Per item, because "who else has this" is a different answer for each
      // one and the grouping depends on it.
      elsewhere: othersFor(row),
      // #615 -- what the row is SHORT by; the sheet caps it at what the
      // sending branch can spare. reorder_qty is the BUY size and stays on
      // the purchase path below (submitBatchRequest).
      shortBy: rowShortBy(row),
    })));
    setSelectMode(false);
    setSelectedIds([]);
  }

  function submitBatchRequest() {
    const items = lowStockItems
      .filter(row => selectedIds.includes(row.id))
      .map(row => ({
        itemId: row.inventory_items.id,
        itemName: row.inventory_items.name,
        itemUom: row.inventory_items.uom,
        sku: row.inventory_items.sku ?? null,
        qty: row.reorder_qty ?? 1,
        reorderQty: row.reorder_qty ?? null,
        // #684 -- what the request screen routes each line by, and what it
        // proposes: a made row goes to its maker for what it is SHORT by (#615).
        // #687 -- and the count, so a selection of one has its own figure.
        categoryId: row.inventory_items?.inventory_categories?.id ?? null,
        qtyOnHand: Number(row.qty_on_hand),
        reorderPoint: row.reorder_point ?? null,
      }));
    setScanItemId(null);
    setBatchRequestItems(items);
    setSelectMode(false);
    setSelectedIds([]);
    setActive('inv_request');
  }

  const actions = [
    {
      id: 'receive',
      label: 'Receive stock',
      sublabel: 'Select an item to receive',
      icon: IconPackageImport,
      primary: true,
      disabled: false,
      onClick: () => setActive?.('inv_receive'),
    },
    {
      id: 'use',
      label: 'Use stock',
      sublabel: 'Select an item to use',
      icon: IconPackageExport,
      primary: false,
      disabled: false,
      onClick: () => setActive?.('inv_use'),
    },
    {
      id: 'adjust',
      label: 'Adjust count',
      // #369 — was `canAdjust ? ... : 'Manager only'`. Every role may adjust now,
      // so the only way canAdjust is false is a profile that has not loaded yet.
      // The card is still disabled in that instant; the sublabel stays true in
      // both states rather than briefly telling a team member they are locked out.
      sublabel: 'Correct qty on hand',
      icon: IconAdjustmentsHorizontal,
      primary: false,
      disabled: !canAdjust,
      onClick: () => setActive?.('inv_adjust'),
    },
    {
      id: 'catalog',
      label: 'Browse catalog',
      sublabel: 'Search all items',
      icon: IconLayoutList,
      primary: false,
      disabled: false,
      onClick: () => setActive?.('inv_catalog'),
    },
  ];

  // #370, gate decision W4 and callout A. On the rail, Request materials is the
  // fifth action rather than a separate full-width card below the grid. The 2x2
  // grid is a thumb affordance -- it exists so two targets sit under one hand --
  // and at a desk there is no thumb and no reach constraint, so it becomes five
  // items pretending to be four quadrants. A list also gives each action its
  // sublabel on one line instead of wrapping it under an icon.
  const railActions = [
    ...actions,
    {
      id: 'request',
      label: 'Request materials',
      sublabel: 'Catalog item or describe something new',
      icon: IconShoppingCartPlus,
      primary: false,
      accent: '#EF9F27',
      disabled: false,
      onClick: () => setActive?.('inv_request_materials'),
    },
  ];

  const showSelectControls = !loadingStock && lowStockItems.length > 0;

  // The track list is shared by the header row and every body row, so the two
  // cannot drift apart. Branch only exists in all-branches mode.
  const tableColumns = [
    selectMode ? '26px' : null,
    'minmax(220px, 1fr)',
    allBranches ? '104px' : null,
    '84px',
    '84px',
    '164px',
    '76px',
  ].filter(Boolean).join(' ');

  const headerCell = {
    fontSize: 10,
    fontWeight: 700,
    color: '#5C5C5C',
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 3,
  };

  // #370, callout B. On hand and Reorder point are read as a pair -- the whole
  // question is which is larger -- and proportional digits make 4 and 11 look
  // comparable in width, which defeats exactly that comparison.
  const numeric = { fontVariantNumeric: 'tabular-nums', textAlign: 'right' };

  // Not a component. Locked rule 2 bans defining a component inside a parent's
  // body because React remounts it every render; this returns an element from a
  // plain call in the same render pass, which is a different thing. Kept tiny
  // and called as a function, never rendered as <SortMark />.
  function sortMark(key) {
    if (sort.key !== key) return null;
    return sort.dir === 'asc' ? <IconChevronUp size={11} /> : <IconChevronDown size={11} />;
  }

  // ── #582 and gate 553 D25 — the row keeps its Low or Out and SAYS what is
  // coming. One line per source, from onItsWayTo(). ─────────────────────────
  function comingLines(row) {
    const elsewhereName = String(profile?.branch_id) === String(row.branch_id) ? null : branchNameOf(row.branch_id);
    return comingFor(row).map(e => onItsWayLine(e, { branchNames: BRANCH_NAMES, elsewhereName })).filter(Boolean);
  }

  // Our own shop already has it: Make these steps down to Make more (D25).
  function isMakingHere(row) {
    return comingFor(row).some(e => e.kind === 'made_here' || e.kind === 'asked_here');
  }

  // 371 D19: with something on its way the action STEPS DOWN rather than
  // disappearing, because a truck may not carry enough.
  function isSteppedDown(row) {
    return comingFor(row).length > 0;
  }

  // #582, as Colby corrected it: who has already asked the branch this
  // pathway names, for this row's item.
  function asksFor(row, p) {
    return p?.fromBranchId == null ? [] : (openAsks[`${row.item_id}:${p.fromBranchId}`] || []);
  }

  function comingBlock(row, fontSize) {
    const lines = comingLines(row);
    if (!lines.length) return null;
    return lines.map((l, i) => (
      <div key={i} style={{ fontSize, color: '#14503B', fontWeight: 600, lineHeight: 1.3, marginTop: 2 }}>{l}</div>
    ));
  }

  // #668 C -- 371 D19's STEP-DOWN, IN WORDS RATHER THAN A COLOUR. The arrival
  // lines above it are untouched: one per request, each with what it still
  // owes and its own promised date (Colby asked, and this is the answer). A row
  // whose only arrivals are our own shop's run says so in the shop's terms;
  // the drawing showed transfers, and "on the way" is not what a run is.
  function alreadyComingLine(row, fontSize) {
    if (!isSteppedDown(row)) return null;
    const ownShopOnly = comingFor(row).every(e => e.kind === 'made_here' || e.kind === 'asked_here');
    return (
      <div style={{ fontSize, color: '#5C5C5C', fontStyle: 'italic', lineHeight: 1.3, marginTop: 2 }}>
        {ownShopOnly
          ? 'Already being made here. Make more only if it isn’t enough.'
          : 'Already on the way. Ask again only if it isn’t enough.'}
      </div>
    );
  }

  // #668 A -- THE KEY. It could not exist before: with green meaning two
  // things and white overriding both, "a legend for a rule with an override is a
  // legend that lies" (#668). Each colour means one supplier now, so it is true.
  function supplierKey(style) {
    // Colby, on first sight: "the blue is hard to see on the color legend."
    // The pale fills at 10px with a 1px edge nearly vanished, so each swatch is
    // a 14px chip edged in 2px of its OWN text colour -- the pale blue and amber
    // read as outlined chips, which is what their buttons look like.
    const swatch = (bg, br) => (
      <span aria-hidden="true" style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 3, background: bg, border: `2px solid ${br}`, boxSizing: 'border-box', marginRight: 6, verticalAlign: '-2px' }} />
    );
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', fontSize: 11, color: '#3A3A3A', lineHeight: 1.4, ...style }}>
        <span>{swatch(MAKE_GREEN, MAKE_GREEN)}A D&amp;R branch makes it</span>
        <span>{swatch('#EAF2FA', '#1F4E7E')}Another branch&rsquo;s shelf</span>
        <span>{swatch('#FAEEDA', '#633806')}Purchasing buys it</span>
      </div>
    );
  }
  const showSupplierKey = pathwaysReady && !loadingStock && !listFailed && lowStockItems.length > 0 && !selectMode;

  // 550/551 Screen 1 — ONE control on a phone row, naming the outcome, at
  // least 40px for a finger. It holds its place, greyed and unlabelled, until
  // the category tree has answered (callout 11).
  function rowControl(row) {
    const h = controlHeight(coarse, 'min');
    if (!pathwaysReady) {
      return <span aria-hidden="true" style={{ width: 120, height: h, borderRadius: 7, background: '#EFEFEF' }} />;
    }
    const paths = pathwaysFor(row);
    const real = realPathways(paths);
    // #668 -- one pathway takes its SUPPLIER's colour; a choice between several
    // is no supplier, so it is the plain outline. Nothing arriving changes the
    // colour any more: alreadyComingLine() says it in words instead (371 D19).
    const look = real.length === 1 ? lookFor(real[0].key) : 'choice';
    return (
      <button
        onClick={e => { e.stopPropagation(); handleCardClick(row); }}
        title={real.length === 1 && asksFor(row, real[0]).length ? alreadyAskedSentence(asksFor(row, real[0]), branchNameOf) : undefined}
        style={{ ...CONTROL_LOOK[look], minHeight: h, padding: '0 12px', borderRadius: 7,
          fontSize: 12, fontWeight: 700, lineHeight: 1.15, cursor: 'pointer', whiteSpace: 'nowrap' }}>
        {rowControlLabel(paths, { makingHere: isMakingHere(row), steppedDown: isSteppedDown(row), askedFor: p => asksFor(row, p).length > 0 })}
      </button>
    );
  }

  // 371 D19 — the count splits into low and covered.
  const coveredCount = useMemo(
    () => lowStockItems.filter(r => isCovered(r, incomingByBranch[String(r.branch_id)]?.[r.item_id] || [])).length,
    [lowStockItems, incomingByBranch],
  );
  const countSplit = coveredCount > 0
    ? ` · ${lowStockItems.length - coveredCount} low · ${coveredCount} covered`
    : '';

  // 550/551 F1 — said once, above the list, in both layouts.
  const checkFailedNote = checkFailed && !loadingStock && !listFailed ? (
    <div role="status" style={{
      background: '#FFF8EC', border: '1px solid #F3D9A8', borderRadius: 8,
      padding: '8px 10px', fontSize: 13, color: '#8A5A08', marginBottom: 8, lineHeight: 1.4,
    }}>
      {CHECK_FAILED}
    </div>
  ) : null;

  // #595 — what a failed load says instead of "All stocked up".
  function listFailedState(pad) {
    return (
      <div role="alert" style={{ background: 'white', borderRadius: 10, padding: pad, border: '1px solid #E8E8E8', textAlign: 'center' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#A32D2D', marginBottom: 4 }}>The low-stock list did not load</div>
        <div style={{ fontSize: 12, color: '#5C5C5C', marginBottom: 10 }}>Check your connection and try again. This does not mean nothing is low.</div>
        <button onClick={() => loadLowStock()} className="btn btn-secondary"
          style={{ minHeight: controlHeight(coarse, 'min'), justifyContent: 'center' }}>
          Try again
        </button>
      </div>
    );
  }

  return (
    <div style={{ background: '#F5F5F3', minHeight: '100vh', paddingBottom: selectMode && selectedIds.length > 0 ? 140 : 80 }}>

      <div style={{ background: 'white', padding: '14px 16px 12px', borderBottom: '1px solid #E8E8E8' }}>
        <div style={{ ...column, position: 'relative' }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: '#0F0F0F', marginBottom: 4 }}>Stock Home</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {canSwitchBranch ? (
            <button
              onClick={() => setShowBranchPicker(v => !v)}
              style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#F0F0F0', border: 'none', borderRadius: 12, padding: '3px 10px', fontSize: 11, fontWeight: 600, color: '#5C5C5C', cursor: 'pointer' }}
            >
              {allBranches ? 'All branches' : branchName}<IconChevronDown size={12} />
            </button>
          ) : (
            <span style={{ background: '#F0F0F0', borderRadius: 12, padding: '3px 10px', fontSize: 11, fontWeight: 600, color: '#5C5C5C' }}>{branchName}</span>
          )}
          {canSwitchBranch && <span style={{ fontSize: 11, color: '#9A9A9A' }}>· tap to switch branch</span>}
        </div>

        {/* #689 D19, gate 665 Screen 7 -- one row, two taps to the card. Each
            chip is a real button at the control floor (40 with a finger, 32
            with a mouse), its dot the colour of the tab it opens. */}
        {transferShortcuts.length > 0 && (
          <nav aria-label="Transfer work" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 10 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#3A3A3A' }}>
              {shortcutHeading(BRANCH_NAMES[String(profile?.branch_id)], !phoneLayout)}
            </span>
            {transferShortcuts.map(c => (
              <button key={c.tab} type="button"
                onClick={() => (openTransfersAt ? openTransfersAt(c.tab, profile?.branch_id) : setActive?.('transfers'))}
                style={{ minHeight: controlHeight(coarse, 'min'), display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '0 12px', borderRadius: 999, border: '1px solid #CFCFCF', background: 'white',
                  fontSize: 13, fontWeight: 600, color: '#1A1A1A', cursor: 'pointer' }}>
                <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: '50%', background: SHORTCUT_DOT[c.tab], flexShrink: 0 }} />
                {c.label}
              </button>
            ))}
          </nav>
        )}

        {showBranchPicker && (
          // #356. Was top: 70 / left: 16 against the page itself, which only ever landed
          // under the button while the content began at the viewport's left edge. Now
          // anchored to the header block above, so it follows the centred column.
          <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 6, zIndex: 100, background: 'white', border: '1px solid #E0E0E0', borderRadius: 12, boxShadow: '0 4px 16px rgba(0,0,0,0.10)', overflow: 'hidden', minWidth: 160 }}>
            {/* #370, W8. All branches is a desktop-workbench affordance only: desktop
                emphasises holistic multi-location visibility while mobile stays
                location-specific. That is W9, and it is why this is gated on
                isWorkbench and not on the permission alone. */}
            {/* #520, Session 79.10 — IT NO LONGER VANISHES SILENTLY.
                Colby went looking for this as an admin, saw only the four
                branches, and asked whether it had been deleted. It had not: his
                window was under 1180 CSS pixels, which at 150% Windows scaling
                is an ordinary working window rather than a phone.

                THE GATE IS RIGHT AND STAYS — #370's W8/W9 make all-branches a
                desktop affordance deliberately. What was wrong is that it
                disappeared with nothing said, so somebody who had seen it once
                reasonably concluded it was removed and somebody who never had
                could not learn it exists.

                This is #492's standard and #503's blocked-control pattern:
                aria-disabled with a VISIBLE REASON rather than silent absence.
                It stays keyboard-reachable, and it answers a press. */}
            {canSeeAllBranches && (
              isWorkbench ? (
                <button onClick={() => { setViewAll(true); setShowBranchPicker(false); }}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '12px 16px', fontSize: 14, fontWeight: allBranches ? 700 : 400, color: allBranches ? '#1D9E75' : '#0F0F0F', background: allBranches ? '#F0FFF4' : 'white', border: 'none', borderBottom: '1px solid #E0E0E0', cursor: 'pointer' }}>
                  All branches
                </button>
              ) : (
                <div
                  aria-disabled="true"
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '12px 16px', borderBottom: '1px solid #E0E0E0', background: '#FAFAFA' }}>
                  <div style={{ fontSize: 14, color: '#8A8A8A' }}>All branches</div>
                  <div style={{ fontSize: 12, color: '#7A7A7A', marginTop: 3, lineHeight: 1.35 }}>
                    Needs a wider window — widen this one and it comes back.
                  </div>
                </div>
              )
            )}
            {BRANCHES.map(b => (
              <button key={b.id} onClick={() => { setBranchId(b.id); setViewAll(false); setShowBranchPicker(false); }}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '12px 16px', fontSize: 14, fontWeight: !allBranches && b.id === branchId ? 700 : 400, color: !allBranches && b.id === branchId ? '#39D353' : '#0F0F0F', background: !allBranches && b.id === branchId ? '#F0FFF4' : 'white', border: 'none', borderBottom: '1px solid #F0F0F0', cursor: 'pointer' }}>
                {b.name}
              </button>
            ))}
          </div>
        )}
        </div>
      </div>

      {isWorkbench ? (
        /* #370 — the workbench. Action rail left, low-stock table right. The rail
           stays first in reading order because Stock Home is a workflow surface,
           not a reporting one: demoting it to a toolbar would trade Stede's only
           real advantage over Fishbowl for a table Fishbowl already has. That is
           W3, and it reversed this gate's own first draft. */
        <div style={{ ...column, display: 'grid', gridTemplateColumns: '236px 1fr', gap: 14, padding: '14px 14px 0', alignItems: 'start' }}>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {railActions.map(action => {
              const Icon = action.icon;
              return (
                <button key={action.id} onClick={action.disabled ? undefined : action.onClick}
                  style={{ borderRadius: 12, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', width: '100%', cursor: action.disabled ? 'default' : 'pointer', border: action.primary ? 'none' : `${action.accent ? 1.5 : 1}px solid ${action.accent || '#E0E0E0'}`, background: action.primary ? '#39D353' : action.disabled ? '#F0F0F0' : 'white', opacity: action.disabled ? 0.6 : 1, transition: 'opacity 0.15s' }}>
                  <Icon size={22} color={action.primary ? '#0F0F0F' : action.disabled ? '#9A9A9A' : (action.accent || '#0F0F0F')} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: action.primary ? '#0F0F0F' : action.disabled ? '#9A9A9A' : '#0F0F0F', lineHeight: 1.2 }}>{action.label}</div>
                    <div style={{ fontSize: 11, lineHeight: 1.3, color: action.primary ? '#0A5C1A' : '#7A7A7A', marginTop: 2 }}>{action.sublabel}</div>
                  </div>
                </button>
              );
            })}
          </div>

          <div>
            <div style={{ padding: '0 0 6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span ref={lowStockLabelRef} style={{ fontSize: 11, fontWeight: 700, color: '#5C5C5C', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                Low stock · {scopeLabel}{countSplit}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {/* #518 -- the count IS the control. It sits where a person already
                    looks for "how many", at the TOP of the list rather than under it,
                    which is the whole complaint: the way out used to live at the
                    bottom of the thing you were trying to escape. Inert when nothing
                    is hidden, so it never promises an action that would do nothing.

                    THE COUNT LIVES ON THE BOTTOM BUTTON, NOT HERE. Colby's call, and
                    it removes a real duplication: both controls said the number, and
                    on a 375px phone the header was carrying a section label, a count
                    and two actions in one row. "Show all 32" at the end of the list
                    is where a person has just finished reading the six and wants the
                    rest, so that is where the number earns its space. Up here it only
                    needs to be a way out.

                    THE COMMENT SITS OUTSIDE THE `&&`, and that is not style. A
                    `cond && ( … )` takes ONE expression, so a JSX comment followed by
                    a ternary is two children and Babel refuses the file. It did, and
                    the whole page rendered blank -- which `npm test` reported as 1,370
                    passing, because no test imports this page. A build error is not a
                    test failure here; the only instrument that sees it is loading the
                    screen. */}
                {showSelectControls && !selectMode && (
                  hiddenCount > 0 ? (
                    <button onClick={() => setShowAllLowStock(true)}
                      style={{ fontSize: 11, color: '#8A5A08', fontWeight: 700, background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3 }}>
                      show all
                    </button>
                  ) : (
                    <span style={{ fontSize: 11, color: '#8A5A08', fontWeight: 700 }}>{lowStockItems.length} item{lowStockItems.length !== 1 ? 's' : ''}</span>
                  )
                )}
                {showSelectControls && (
                  <button onClick={toggleSelectMode} style={{ background: 'none', border: 'none', color: MAKE_GREEN, fontSize: 11, fontWeight: 700, cursor: 'pointer', padding: 0 }}>
                    {selectMode ? 'Cancel' : 'Request several'}
                  </button>
                )}
              </div>
            </div>

            {checkFailedNote}
            {loadingStock ? (
              <div style={{ background: 'white', borderRadius: 10, border: '1px solid #E8E8E8', padding: '24px', textAlign: 'center', color: '#5C5C5C', fontSize: 13 }}>Loading...</div>
            ) : listFailed ? listFailedState('24px 16px') : lowStockItems.length === 0 ? (
              /* #370, callout G. The empty state stays, and stays honest. It is
                 true at all only because #357 landed first: before that an item at
                 zero with no reorder point appeared on no list, and a wider, more
                 authoritative-looking table would have made a half-truth look like
                 a full one. That is why W10 made #357 a prerequisite. */
              <div style={{ background: 'white', borderRadius: 10, padding: '28px 16px', border: '1px solid #E8E8E8', textAlign: 'center' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: MAKE_GREEN, marginBottom: 4 }}>All stocked up</div>
                <div style={{ fontSize: 12, color: '#5C5C5C' }}>No low or out-of-stock items at {scopeLabel}</div>
              </div>
            ) : (
              <div style={{ background: 'white', borderRadius: 10, border: '1px solid #E8E8E8', overflow: 'hidden' }}>

                {/* #668 A -- the key, inside the table's card, above its columns. */}
                {showSupplierKey && supplierKey({ padding: '7px 12px', borderBottom: '1px solid #E8E8E8' })}
                <div style={{ display: 'grid', gridTemplateColumns: tableColumns, gap: 10, alignItems: 'center', padding: '9px 12px', borderBottom: '1px solid #E8E8E8', background: '#FAFAF9' }}>
                  {selectMode && <span />}
                  <button style={headerCell} onClick={() => toggleSort('item')}>Item{sortMark('item')}</button>
                  {allBranches && <button style={headerCell} onClick={() => toggleSort('branch')}>Branch{sortMark('branch')}</button>}
                  <button style={{ ...headerCell, justifyContent: 'flex-end' }} onClick={() => toggleSort('qty')}>On hand{sortMark('qty')}</button>
                  <button style={{ ...headerCell, justifyContent: 'flex-end' }} onClick={() => toggleSort('reorder')}>Reorder{sortMark('reorder')}</button>
                  <button style={headerCell} onClick={() => toggleSort('elsewhere')}>Elsewhere{sortMark('elsewhere')}</button>
                  <button style={{ ...headerCell, justifyContent: 'flex-end' }} onClick={() => toggleSort('status')}>Status{sortMark('status')}</button>
                </div>

                {visibleItems.map(row => {
                  const item = row.inventory_items;
                  const status = getStockStatus(row.qty_on_hand, row.reorder_point);
                  const s = STATUS_COLORS[status];
                  const catLabel = getCategoryLabel(item?.inventory_categories);
                  const isSelected = selectedIds.includes(row.id);
                  const others = othersFor(row);
                  const noThreshold = row.reorder_point === null || row.reorder_point === undefined;
                  // #613, gate 550/551 D20 (approved by Colby, Sep 21) -- the row
                  // SAYS when its click opens more than its buttons show: the
                  // chooser with "Buy from a vendor instead?" at its foot.
                  const opensMore = pathwaysReady && !selectMode && rowOpensMore(pathwaysFor(row));
                  return (
                    /* #370, W7 and callout D. The row is still one click from a
                       request, and Select still batches. Fishbowl's dashboard row
                       is a fact; Stede's is a fact you can act on without leaving
                       the screen. Gaining a table by losing that would be a
                       downgrade dressed as an upgrade. */
                    /* #519, Session 79.10 — THE ROW IS NO LONGER ONE BIG BUTTON,
                       and that is what the fix required rather than a preference.
                       A <button> cannot contain another <button>, so while the
                       whole row was one control there was nowhere on the desktop
                       table to put an Ask action at all. Mobile had already solved
                       this by splitting into sibling buttons inside a flex row;
                       this is the same move on the grid.

                       WHOLE-ROW CLICK IS PRESERVED — #370's W7 says the row stays
                       one click from a request, and losing that to gain a chip
                       would be a downgrade dressed as an upgrade. role="button"
                       plus Enter and Space keeps it reachable from the keyboard,
                       which a plain onClick div would not. */
                    <div key={row.id}
                      role="button"
                      tabIndex={0}
                      title={opensMore ? 'All the ways to get these' : undefined}
                      onClick={() => handleCardClick(row)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardClick(row); }
                      }}
                      style={{ display: 'grid', gridTemplateColumns: tableColumns, gap: 10, alignItems: 'center', width: '100%', textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #F2F2F0', background: isSelected ? '#F0FFF7' : 'white', cursor: 'pointer' }}>
                      {selectMode && (
                        <div style={{
                          width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: isSelected ? '#1D9E75' : 'white',
                          border: isSelected ? 'none' : '1.5px solid #C0C0C0',
                        }}>
                          {isSelected && <IconCheck size={13} color="white" />}
                        </div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                        <div style={{ width: 30, height: 30, borderRadius: 7, background: '#F0F0F0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          {item?.image_url ? <IconPhoto size={15} color="#39D353" /> : <IconPhotoOff size={15} color="#C0C0C0" />}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#0F0F0F', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item?.name}</div>
                          <div style={{ fontSize: 11, color: '#5C5C5C', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{catLabel}</div>
                          {comingBlock(row, 11)}
                          {alreadyComingLine(row, 11)}
                        </div>
                      </div>
                      {allBranches && <div style={{ fontSize: 12, color: '#5C5C5C' }}>{branchNameOf(row.branch_id)}</div>}
                      <div style={{ ...numeric, fontSize: 14, fontWeight: 700, color: s.color }}>{row.qty_on_hand}</div>
                      {/* #370, callout F. A Status pill cannot explain itself: "Low"
                          is a verdict reached by comparing two numbers, and the
                          screen used to show the verdict and only one of them. An
                          em dash rather than a zero -- no threshold set is not a
                          threshold of nothing. */}
                      <div style={{ ...numeric, fontSize: 13, color: '#5C5C5C' }}>
                        {noThreshold ? '—' : row.reorder_point}
                      </div>
                      {/* #519 — THE ASK LIVES IN THE ELSEWHERE COLUMN, which is
                          where it belongs semantically: this cell is already
                          about who else has the material, and asking them is
                          what you do about it. No column is displaced.

                          stopPropagation because the row itself is clickable and
                          opens the PURCHASING request — two different acts, and
                          firing both would open one screen over the other. */}
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 11, color: '#5C5C5C', lineHeight: 1.35, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {others.length
                            ? others.map(o => `${branchNameOf(o.branchId)} ${o.qty}`).join(' · ')
                            : 'none'}
                        </div>
                        {/* #550 D10 and D15. THE ASK CHIP IS REPLACED HERE, not kept
                            beside the new controls -- keeping both would recreate
                            #550 in a new costume, two controls on one row that
                            nobody designed together.

                            Desk and Wide have the horizontal room and a mouse, so
                            the pathways show inline as buttons. CORRECTED at #613:
                            this used to say they "save the chooser entirely" - the
                            row's own click still opens it when it holds more than
                            the buttons (D14's exception), and D20's › says so.
                            stopPropagation because the row is still clickable and
                            firing both would open one screen over another. */}
                        {/* #580 part (b) -- 550/551 Screen 2 and F4. The REAL pathways
                            only: D14's exception is not a peer button here, it is
                            reached through the row's chooser (or, on the maker's
                            own row, at the foot of the make sheet). 32px with a
                            mouse and 40 with a finger, whatever the width, and
                            12px text -- they were 28px and 10px at every pointer.
                            The Make control is the darkened green (6.20). */}
                        {!selectMode && (pathwaysReady ? (
                          <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {realPathways(pathwaysFor(row)).map(p => {
                              // #668 -- the supplier's colour, whatever is arriving.
                              const look = lookFor(p.key);
                              const asks = asksFor(row, p);
                              return (
                                <button key={p.key}
                                  title={asks.length ? alreadyAskedSentence(asks, branchNameOf) : p.detail}
                                  onClick={e => { e.stopPropagation(); openPathway(row, p.key); }}
                                  style={{
                                    ...CONTROL_LOOK[look],
                                    minHeight: coarse ? 40 : 32, display: 'inline-flex', alignItems: 'center',
                                    fontSize: 12, fontWeight: 700, borderRadius: 6, padding: '0 10px',
                                    whiteSpace: 'nowrap', cursor: 'pointer',
                                  }}>
                                  {/* #582 -- each word true of its own route (Colby, Sep 21). */}
                                  {pathwayButtonLabel(p, { steppedDown: isSteppedDown(row), makingHere: isMakingHere(row), asked: asks.length > 0 })}
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <span aria-hidden="true" style={{ display: 'inline-block', marginTop: 4, width: 72, height: coarse ? 40 : 32, borderRadius: 6, background: '#EFEFEF' }} />
                        ))}
                      </div>
                      <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <span style={{ display: 'inline-block', padding: '1px 7px', borderRadius: 8, fontSize: 10, fontWeight: 700, background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>{s.label}</span>
                        {/* #613, D20 -- the mark is not a control: the row is the
                            target. #5C5C5C, 6.69 on white. Read aloud instead. */}
                        {opensMore && (
                          <>
                            <span aria-hidden="true" style={{ marginLeft: 6, fontSize: 18, lineHeight: 1, color: '#5C5C5C', verticalAlign: 'middle' }}>›</span>
                            <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}>
                              Opens all the ways to get these, including buying from a vendor
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
                {/* #518 -- same cap on the workbench. The desktop table is not
                    exempt: 32 rows push everything below the fold here too, and a
                    person on a laptop is the same person. */}
                {hiddenCount > 0 && (
                  <button onClick={() => setShowAllLowStock(true)}
                    style={{ width: '100%', minHeight: 44, border: 'none', borderTop: '1px solid #E8E8E8', background: 'white', color: '#0F0F0F', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    Show all {sortedItems.length}
                  </button>
                )}
                {showAllLowStock && sortedItems.length > LOW_STOCK_PREVIEW && (
                  <button onClick={() => setShowAllLowStock(false)}
                    style={{ width: '100%', minHeight: 44, border: 'none', borderTop: '1px solid #E8E8E8', background: 'white', color: '#5C5C5C', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    Show fewer
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Below WORKBENCH_BREAKPOINT nothing changes. Gate decision W9 is that
           mobile is untouched -- not "mostly untouched" -- and W2 gives the
           768-1179 tier the bounded single column #356 shipped. Any diff that
           changes a phone pixel has exceeded this gate. */
        <>
          {/* Four across on desktop rather than two: at DESKTOP_COLUMN a two-up grid still
              renders each action as a 545px banner with a 24px icon in its corner. */}
          <div style={{ ...column, display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 10, padding: '14px 14px 0' }}>
            {actions.map(action => {
              const Icon = action.icon;
              return (
                <button key={action.id} onClick={action.disabled ? undefined : action.onClick}
                  style={{ borderRadius: 12, padding: '16px 14px', display: 'flex', flexDirection: 'column', gap: 6, textAlign: 'left', cursor: action.disabled ? 'default' : 'pointer', border: action.primary ? 'none' : '1px solid #E0E0E0', background: action.primary ? '#39D353' : action.disabled ? '#F0F0F0' : 'white', opacity: action.disabled ? 0.6 : 1, transition: 'opacity 0.15s' }}>
                  <Icon size={24} color={action.primary ? '#0F0F0F' : action.disabled ? '#9A9A9A' : '#0F0F0F'} />
                  <div style={{ fontSize: 13, fontWeight: 700, color: action.primary ? '#0F0F0F' : action.disabled ? '#9A9A9A' : '#0F0F0F', lineHeight: 1.2 }}>{action.label}</div>
                  <div style={{ fontSize: 11, lineHeight: 1.3, color: action.primary ? '#0A5C1A' : '#7A7A7A' }}>{action.sublabel}</div>
                </button>
              );
            })}
          </div>

          <div style={{ ...column, padding: '10px 14px 0' }}>
            <button
              onClick={() => setActive?.('inv_request_materials')}
              style={{
                width: '100%',
                borderRadius: 12,
                padding: '14px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                textAlign: 'left',
                cursor: 'pointer',
                border: '1.5px solid #EF9F27',
                background: 'white',
                transition: 'opacity 0.15s',
              }}
            >
              <div style={{ width: 40, height: 40, borderRadius: 10, background: '#FFF8EC', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <IconShoppingCartPlus size={22} color="#EF9F27" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0F0F0F', lineHeight: 1.2 }}>Request materials</div>
                <div style={{ fontSize: 11, color: '#7A7A7A', marginTop: 2 }}>Catalog item or describe something new</div>
              </div>
              <IconChevronRight size={18} color="#C0C0C0" />
            </button>
          </div>

          <div style={{ ...column, padding: '14px 14px 6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span ref={lowStockLabelRef} style={{ fontSize: 11, fontWeight: 700, color: '#5C5C5C', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
              Low stock · {branchName}{countSplit}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {/* #518 -- the count IS the control, and the comment sits OUTSIDE the
                  `&&` for the reason spelled out on the workbench copy above: a
                  `cond && ( … )` takes one expression, and a comment plus a ternary
                  is two. */}
              {showSelectControls && !selectMode && (
                hiddenCount > 0 ? (
                  <button onClick={() => setShowAllLowStock(true)}
                    style={{ fontSize: 11, color: '#8A5A08', fontWeight: 700, background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3 }}>
                    show all
                  </button>
                ) : (
                  <span style={{ fontSize: 11, color: '#8A5A08', fontWeight: 700 }}>{lowStockItems.length} item{lowStockItems.length !== 1 ? 's' : ''}</span>
                )
              )}
              {showSelectControls && (
                <button onClick={toggleSelectMode} style={{ background: 'none', border: 'none', color: MAKE_GREEN, fontSize: 11, fontWeight: 700, cursor: 'pointer', padding: 0 }}>
                  {selectMode ? 'Cancel' : 'Request several'}
                </button>
              )}
            </div>
          </div>

          <div style={{ ...column, padding: '0 14px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {/* #668 A -- the key, once, under the section label. */}
            {showSupplierKey && supplierKey({ padding: '0 2px 2px' })}
            {checkFailedNote}
            {loadingStock ? (
              <div style={{ padding: '24px', textAlign: 'center', color: '#5C5C5C', fontSize: 13 }}>Loading...</div>
            ) : listFailed ? listFailedState('20px 16px') : lowStockItems.length === 0 ? (
              <div style={{ background: 'white', borderRadius: 10, padding: '20px 16px', border: '1px solid #E8E8E8', textAlign: 'center' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: MAKE_GREEN, marginBottom: 4 }}>All stocked up</div>
                <div style={{ fontSize: 12, color: '#5C5C5C' }}>No low or out-of-stock items at {branchName}</div>
              </div>
            ) : visibleItems.map(row => {
              const item = row.inventory_items;
              const status = getStockStatus(row.qty_on_hand, row.reorder_point);
              const s = STATUS_COLORS[status];
              const catLabel = getCategoryLabel(item?.inventory_categories);
              const isSelected = selectedIds.includes(row.id);
              return (
                <React.Fragment key={row.id}>
                {/* #518 -- A DIV WRAPPING TWO BUTTONS, not one button, and the
                    restructure is forced rather than stylistic.

                    The chip has to be a real button: it opens the transfer sheet via
                    setTransferRow, and the row does NOT -- handleCardClick calls
                    openRestockModal, which is the PURCHASING path. Making the chip a
                    span left setTransferRow with no caller at all and silently
                    removed the only way to raise a transfer from this screen. Caught
                    by grepping for the setter rather than by looking at the page,
                    which would have shown a chip that looked right and did nothing --
                    #492 exactly.

                    A button inside a button is invalid HTML, so the row becomes a div
                    and its two clickable regions become siblings. Both open the item;
                    the chip opens the transfer. */}
                <div style={{ background: 'white', borderRadius: 10, padding: '11px 12px', border: isSelected ? '1px solid #1D9E75' : '1px solid #E8E8E8', width: '100%', boxSizing: 'border-box' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <button
                    onClick={() => handleCardClick(row)}
                    onTouchStart={(e) => startLongPress(row, e)}
                    onTouchMove={moveLongPress}
                    onTouchEnd={endLongPress}
                    onTouchCancel={endLongPress}
                    style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10, background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}>
                    {selectMode && (
                      <div style={{
                        width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: isSelected ? '#1D9E75' : 'white',
                        border: isSelected ? 'none' : '1.5px solid #C0C0C0',
                      }}>
                        {isSelected && <IconCheck size={13} color="white" />}
                      </div>
                    )}
                    <div style={{ width: 36, height: 36, borderRadius: 8, background: '#F0F0F0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {item?.image_url ? <IconPhoto size={16} color="#39D353" /> : <IconPhotoOff size={16} color="#C0C0C0" />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* #518 -- WRAPS TO TWO LINES rather than truncating, and this is
                          a correction to the chip rather than a preference.
                          MEASURED after adding it: the name column drops from 194px to
                          106px, and three of the first six Dallas rows truncated --
                          "Stake DT 23x2...", "Frame Std 18x...". On a list where every
                          row begins "Stake" or "Frame", THE SIZE IS THE IDENTITY, so a
                          cut name is the one thing on the row that must not be cut.
                          Clamped at two lines so a long name still cannot run away. */}
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#0F0F0F', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.25 }}>{item?.name}</div>
                      {/* 550/551 Screen 1: "Frames · Houston makes these",
                          "Sheet goods · 40 at Austin". The hint moved here from
                          the right edge so the row's one control has room. */}
                      <div style={{ fontSize: 11, color: '#5C5C5C', lineHeight: 1.3 }}>
                        {[catLabel, transferHintFor(row)].filter(Boolean).join(' \u00b7 ')}
                      </div>
                      {comingBlock(row, 11)}
                      {alreadyComingLine(row, 11)}
                    </div>
                  </button>
                  <button
                    onClick={() => handleCardClick(row)}
                    style={{ flexShrink: 0, textAlign: 'right', background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: s.color }}>{row.qty_on_hand}</div>
                    <span style={{ display: 'inline-block', padding: '1px 7px', borderRadius: 8, fontSize: 10, fontWeight: 700, background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>{s.label}</span>
                  </button>
                  </div>
                  {/* #580 part (b) -- 550/551 Screen 1 and D11: ONE control on
                      the row, naming the outcome, at least 40px for a finger.
                      It was missing, so nothing on a phone row said it could
                      be tapped or what a tap would do.
                      ON ITS OWN LINE, as the gate draws it: beside the name it
                      cut "Frame Std 18x24 Bot Rider" to "Bot..." -- and #518's
                      rule is that on this list the size IS the identity. */}
                  {!selectMode && (
                    <div style={{ display: 'flex', marginTop: 9, paddingLeft: 46 }}>
                      {rowControl(row)}
                    </div>
                  )}
                </div>
                </React.Fragment>
              );
            })}
            {/* #518 -- the footer half of the cap. Deliberately NOT a link to a
                separate all-low-stock screen: there is no such screen, and building
                one would be a second surface answering a question this one already
                answers, which is the mistake #391 exists to record. Expanding in
                place also keeps every transfer entry point reachable -- this list is
                the only place "Ask Houston" appears. 44px tall against the app's
                current 32px, because #513's app-wide fix is hosted in October and a
                control added tonight should not arrive already below the minimum. */}
            {hiddenCount > 0 && (
              <button onClick={() => setShowAllLowStock(true)}
                style={{ width: '100%', minHeight: 44, borderRadius: 10, border: '1px solid #D8D8D8', background: 'white', color: '#0F0F0F', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginTop: 2 }}>
                Show all {sortedItems.length}
              </button>
            )}
            {showAllLowStock && sortedItems.length > LOW_STOCK_PREVIEW && (
              <button onClick={() => setShowAllLowStock(false)}
                style={{ width: '100%', minHeight: 44, borderRadius: 10, border: '1px solid #D8D8D8', background: 'white', color: '#5C5C5C', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginTop: 2 }}>
                Show fewer
              </button>
            )}
          </div>
        </>
      )}

      {selectMode && selectedIds.length > 0 && (
        <div style={{ position: 'fixed', bottom: 64, left: 0, right: 0, padding: '10px 14px', zIndex: 50 }}>
          <div style={column}>
          <button onClick={submitBatchRequest}
            style={{ width: '100%', padding: 14, borderRadius: 12, fontSize: 14, fontWeight: 700, border: 'none', background: '#1D9E75', color: 'white', cursor: 'pointer', boxShadow: '0 2px 12px rgba(0,0,0,0.18)' }}>
            Request {selectedIds.length} item{selectedIds.length !== 1 ? 's' : ''} →
          </button>
          {/* #517 / D53 -- THE SAME SELECTION, ASKED OF ANOTHER BRANCH.
              The multi-select and long-press shipped at #518 and went only to
              purchasing; this is the second destination the gate named, and it
              reuses the entry point rather than inventing a doorway.

              Shown only when at least one of the chosen items could actually
              come from somewhere else -- offering it on a selection nobody can
              fill is #492's class, a control that accepts a press and cannot
              work. */}
          {selectedIds.some(id => transferChipFor(lowStockItems.find(r => r.id === id) || {})) && (
            <button onClick={startBatchTransfer}
              style={{ width: '100%', marginTop: 8, padding: 14, borderRadius: 12, fontSize: 14, fontWeight: 700, border: '1px solid #D8EDE3', background: '#E8F5EE', color: '#14503B', cursor: 'pointer' }}>
              Ask another branch instead →
            </button>
          )}
          </div>
        </div>
      )}

      {/* First-run callouts gate, Wave 1 — inv_home_first. All roles: every field
          role lands here. Held back until the fetch settles so the tip never
          arrives over a spinner describing a list that is not on screen yet. */}
      {/* #371, Session 79.6. Opens already answered wherever the app can answer
          for itself, which is what makes acceptance line T1's two taps possible:
          the item from the row, the receiving branch always your own, the sending
          branch prefilled with whoever can SPARE the most (#558, #565 D3 - this
          said "holds the most" until #615), and the quantity prefilled with
          what this row is short by, capped at what that branch can spare (#615). */}
      {/* #516, Session 79.7 -- THE `key` IS LOAD-BEARING AND ITS ABSENCE WAS A
          DEFECT, found by driving the acceptance test rather than by reading.
          Ask Houston for 77 of one stake, open a different stake, and the sheet
          already said 77 with the date chosen for the first one.

          The sheet returns null when it is not visible, but returning null is
          not unmounting: it stays mounted with its state intact, and it carries
          no useEffect that resets anything. So every `useState` initialiser ran
          ONCE, at page mount, while `item` was still null and `elsewhere` was
          still empty -- which means the prefill this very comment promises had
          never worked either, on any row, since #371 shipped. Two bugs, one
          cause, and the harmless-looking half is the one that made the harmful
          half invisible: a sheet that always opened empty looked deliberate.

          Keying on the item id makes each row a fresh component, so the
          initialisers run against THAT row's props. That is what the sheet was
          always written to expect -- it takes its quantity as an initial value
          and nothing ever gave it a second chance to read one. (That prop was
          suggestedQty, carrying the buy size; #615 made it shortBy.)

          SWEPT, per locked rule 11, and the result is what makes this a lapse
          rather than an unknown. Six render sites in the app use this
          toggle-rather-than-unmount shape. PriceSheet and ItemNumSheet in
          VendorCatalogPage.js -- the only other two that take a RECORD -- both
          already carry `useEffect(..., [visible, vi?.id])` doing exactly this
          reset. AddVendorSheet and AssociateItemSheet take no record, so there
          is no second record to confuse them with. RequestTransferSheet was the
          only one of the six with no reset of any kind: the pattern was
          established and the newest sheet did not follow it.

          KEY RATHER THAN THAT useEffect, deliberately, and it is the one place
          this diverges from the house shape. `fromBranchId` is a LAZY
          INITIALISER that sorts `elsewhere` to prefill whoever holds the most,
          and a reset effect would have to duplicate that sorting logic in a
          second place -- a rule in two homes, which is how the third copy gets
          missed. Remounting re-runs it for free. */}
      {/* #550 D8. THE PATHWAY CHOOSER. Compact, ordered, primary first, and
          every option written as WHAT HAPPENS rather than what it is called
          internally (D9) -- "Ask Houston to make these", never "Transfer
          request (make mode)".

          It is the only shape that works identically at every width, which is
          why the phone and the tablet route through it rather than growing a
          third control on the row. On a phone it is a sheet and on a desk a
          dialog, per #417 D24's 1024 boundary. */}
      {pathwayRow && (() => {
        // #580 part (b) -- 550/551 F2's approved copy, typed rather than composed.
        const paths = pathwaysFor(pathwayRow);
        const real = realPathways(paths);
        const shelf = real.find(p => p.key === 'transfer');
        const rp = pathwayRow.reorder_point == null ? null : Number(pathwayRow.reorder_point);
        const onHand = Number(pathwayRow.qty_on_hand) || 0;
        const optionH = controlHeight(coarse, 'commit');
        return (
          <Overlay
            variant={phoneLayout ? 'sheet' : 'modal'}
            maxWidth={420}
            onClose={() => setPathwayRow(null)}>
            <div style={{ padding: 18 }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#0F0F0F' }}>
                {pathwayRow.inventory_items?.name}
              </div>
              {/* Callout 8 -- THE HEADER CARRIES THE SHORTFALL, the number the
                  person needs to judge the options below it. It said only
                  "0 on hand at Dallas". */}
              <div style={{ fontSize: 13, color: '#3A3A3A', marginTop: 2 }}>
                {onHand} on hand at {branchNameOf(pathwayRow.branch_id)}
                {rp != null && ` \u00b7 reorder at ${rp} \u00b7 short by ${Math.max(rp - onHand, 0)}`}
              </div>
              {/* What is already on its way is said here too: a person deciding
                  whether to ask again needs it most at this moment (371 D19). */}
              {comingBlock(pathwayRow, 13)}
              {checkFailed && (
                <div role="status" style={{ marginTop: 8, fontSize: 13, color: '#8A5A08', lineHeight: 1.4 }}>{CHECK_FAILED}</div>
              )}
              {shelf && (
                <div style={{ fontSize: 13, color: '#3A3A3A', marginTop: 10, lineHeight: 1.45 }}>
                  {real.length === 3 ? 'Three' : 'Two'} ways to get these. {branchNameOf(shelf.fromBranchId)}'s {shelf.qty} are on a shelf today and may not be tomorrow.
                </div>
              )}

              <div style={{ marginTop: 12 }}>
                {real.map(p => {
                  const lead = p.primary || p.recommended;
                  // A pathway this person cannot take is shown with its reason
                  // rather than left out (550/551 F1) or offered and refused by
                  // the database: gate 553 B4, only somebody at the maker asks.
                  if (p.unavailable) {
                    return (
                      <div key={p.key} aria-disabled="true" style={{
                        marginBottom: 8, minHeight: optionH, padding: '10px 12px', borderRadius: 10,
                        background: '#FAFAFA', border: '1px dashed #CFCFCF', boxSizing: 'border-box',
                      }}>
                        <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: '#3A3A3A' }}>{p.label}</span>
                        <span style={{ display: 'block', fontSize: 12.5, color: '#3A3A3A', marginTop: 2, lineHeight: 1.4 }}>{p.unavailable}</span>
                      </div>
                    );
                  }
                  return (
                    <button key={p.key}
                      onClick={() => openPathway(pathwayRow, p.key)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
                        marginBottom: 8, minHeight: optionH, padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
                        background: lead ? '#EAF5F0' : '#FFFFFF',
                        border: `1px solid ${lead ? MAKE_GREEN : '#D6D6D6'}`,
                      }}>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: '#0F0F0F' }}>{p.label}</span>
                        <span style={{ display: 'block', fontSize: 12.5, color: '#3A3A3A', marginTop: 2, lineHeight: 1.4 }}>{p.detail}</span>
                      </span>
                      <IconChevronRight size={18} color="#5C5C5C" aria-hidden="true" />
                    </button>
                  );
                })}
              </div>

              {/* D14 -- THE EXCEPTION IS A SENTENCE, NOT A BUTTON (callout 9):
                  "{Branch} makes these. Buy from a vendor instead?" It cannot be
                  hit by accident and it reads as a question rather than a peer. */}
              {paths.filter(p => p.exception).map(p => (
                <div key={p.key} style={{ borderTop: '1px solid #EFEFEF', marginTop: 8, paddingTop: 8, fontSize: 13, color: '#3A3A3A' }}>
                  {p.lead}{' '}
                  <button type="button" onClick={() => openPathway(pathwayRow, p.key)}
                    style={{
                      background: 'none', border: 'none', padding: '0 2px', minHeight: controlHeight(coarse, 'min'),
                      font: 'inherit', color: '#3A3A3A', textDecoration: 'underline', textUnderlineOffset: 3, cursor: 'pointer',
                    }}>
                    {p.label}
                  </button>
                </div>
              ))}

              <button
                onClick={() => setPathwayRow(null)}
                style={{ marginTop: 6, width: '100%', minHeight: controlHeight(coarse, 'min'), background: 'transparent', border: 'none', fontSize: 13, color: '#5C5C5C', cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </Overlay>
        );
      })()}

      {/* #580 part (a) -- MAKE THESE OURSELVES, gate 553 D21-D27. Keyed on the
          row so every initialiser -- the quantity above all -- runs against
          THAT row (#516's lesson). */}
      {makeRow && (() => {
        const f = resolveFulfilment(categories, makeRow.inventory_items?.inventory_categories?.id);
        const open = comingFor(makeRow).filter(e => e.kind === 'made_here' || e.kind === 'asked_here');
        return (
          <MakeForShelfSheet
            key={`make:${makeRow.id}`}
            visible
            phoneLayout={phoneLayout}
            coarse={coarse}
            row={makeRow}
            branchName={branchNameOf(makeRow.branch_id)}
            branchNames={BRANCH_NAMES}
            makerDepartmentId={f.departmentId}
            openAsks={open}
            // 553 D23 / B4: the same rule that confirms another branch's make
            // request at this branch, pulled into one predicate.
            canConfirm={canConfirmSupply(profile, {
              from_branch_id: makeRow.branch_id, to_branch_id: makeRow.branch_id,
              fulfilment_mode: 'make', manufacturing_department_id: f.departmentId,
            })}
            requesterName={profile?.full_name || null}
            unavailableReason={pathwaysFor(makeRow).find(p => p.key === 'make_own')?.unavailable || null}
            onClose={() => setMakeRow(null)}
            onBuyInstead={() => { const r = makeRow; setMakeRow(null); openRestockModal(r); }}
            onDone={() => { setMakeRow(null); loadLowStock(); }}
          />
        );
      })()}

      <RequestTransferSheet
        /* #517 -- the key covers BOTH paths. A multi-item ask keys on its whole
           selection, so choosing a different set remounts and re-runs every
           initialiser, which is the same property #516 needed for one row. */
        key={transferRows ? `multi:${transferRows.map(i => i.id).join(',')}` : (transferRow?.inventory_items?.id || 'none')}
        visible={!!transferRow || !!transferRows}
        onClose={() => { setTransferRow(null); setTransferRows(null); }}
        onDone={() => { setTransferRow(null); setTransferRows(null); loadLowStock(); }}
        items={transferRows}
        isMobile={isMobile}
        item={transferRow ? {
          id: transferRow.inventory_items?.id,
          name: transferRow.inventory_items?.name,
          sku: transferRow.inventory_items?.sku,
          // #514 -- carried for the approver's EMAIL, which names what was
          // asked for. Undefined is safe: the template omits an empty unit
          // rather than printing "undefined".
          uom: transferRow.inventory_items?.uom,
          category_id: transferRow.inventory_items?.inventory_categories?.id,
        } : null}
        categories={categories}
        branches={BRANCHES}
        elsewhere={transferRow ? othersFor(transferRow) : []}
        myBranchId={transferRow ? transferRow.branch_id : branchId}
        myUserId={profile?.id}
        // #528, Session 79.10 -- the requester's department, carried onto the
        // document so their own manager can approve it. Read once here rather
        // than live at approval time, so a request does not change hands when
        // somebody moves department.
        myDepartmentId={profile?.department_id ?? null}
        // #514 -- the approver's email says who asked. A user id in that row
        // would be worse than an empty one.
        myUserName={profile?.full_name}
        // #615 -- the SHORTFALL, capped in the sheet at what the sending
        // branch can spare. It was reorder_qty: 200 for a shortage of 20.
        shortBy={transferRow ? rowShortBy(transferRow) : null}
      />

      <OnboardingCallout
        calloutKey="inv_home_first"
        text="Anything low or out at your branch is listed here. Tap a row to ask purchasing for more."
        anchorRef={lowStockLabelRef}
        isMobile={isMobile}
        show={!loadingStock}
      />

      </div>
  );
}
